import React, { useMemo, useState } from 'react';
import {
  Paper, Typography, Chip, IconButton, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem, Box, Stack, alpha, useTheme,
} from '@mui/material';
import { CheckCircle, ErrorOutline, EditOutlined, Check, Close, DeleteOutline } from '@mui/icons-material';

import type { AdmissionCycle, AdmissionField, IdNamePair } from '../../types/admissions';
import { isCnicFieldKey } from './form/admissionFormFields';
import { formatCnic } from '../../utils/cnic';
import {
  type ParsedBulkApplicationRow,
  type BulkApplicationFields,
  type BulkApplicationsLookups,
  validateBulkApplicationRow,
  bulkFormSections,
} from '../../utils/bulkApplicationsXlsx';
import { classesTakingApplications } from '../../utils/admissionClasses';

interface Props {
  rows: ParsedBulkApplicationRow[];
  cycle: AdmissionCycle;
  lookups: BulkApplicationsLookups;
  filter: 'all' | 'ok' | 'error';
  onChange: (next: ParsedBulkApplicationRow[]) => void;
}

/**
 * Review table for the bulk admission intake — one row per applicant, every
 * invalid cell marked with its own message, inline edit re-validated by the
 * same validator as the walk-in and public forms, delete-row support.
 */
const BulkApplicationsReviewTable: React.FC<Props> = ({
  rows, cycle, lookups, filter, onChange,
}) => {
  const theme = useTheme();
  const [editingRowNumber, setEditingRowNumber] = useState<number | null>(null);
  const [draft, setDraft] = useState<BulkApplicationFields | null>(null);

  const columns = useMemo(
    () => bulkFormSections(cycle)
      .flatMap(s => (s.fields || []).filter(f => f.enabled !== false)),
    [cycle],
  );

  const classOptions: IdNamePair[] = useMemo(() => classesTakingApplications(cycle), [cycle]);

  const visibleRows = useMemo(() => {
    if (filter === 'ok') return rows.filter(r => r.clientStatus === 'ok');
    if (filter === 'error') return rows.filter(r => r.clientStatus === 'error');
    return rows;
  }, [rows, filter]);

  // Live validation of the row being edited, so mistakes show as you type.
  const draftErrors = useMemo(
    () => (draft ? validateBulkApplicationRow(draft, cycle, lookups).fieldErrors ?? {} : {}),
    [draft, cycle, lookups],
  );

  const startEdit = (row: ParsedBulkApplicationRow) => {
    setEditingRowNumber(row.rowNumber);
    setDraft({ ...row.fields });
  };
  const cancelEdit = () => { setEditingRowNumber(null); setDraft(null); };
  const saveEdit = (rowNumber: number) => {
    if (!draft) return;
    const validated = validateBulkApplicationRow(draft, cycle, lookups);
    onChange(rows.map(r => (r.rowNumber === rowNumber ? { ...r, fields: draft, ...validated } : r)));
    cancelEdit();
  };
  const deleteRow = (rowNumber: number) => onChange(rows.filter(r => r.rowNumber !== rowNumber));

  if (visibleRows.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: `1px dashed ${theme.palette.divider}` }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length === 0
            ? 'No rows to review.'
            : filter === 'ok' ? 'No rows are ready yet.' : 'No rows need fixing — everything looks good.'}
        </Typography>
      </Paper>
    );
  }

  const displayValue = (field: AdmissionField, value: string | undefined): string => {
    if (!value) return '';
    if (isCnicFieldKey(field.key)) return formatCnic(value);
    if (field.type === 'dropdown' || field.type === 'multiselect') {
      return value.split(',').map(v => field.options?.find(o => o.value === v.trim())?.label ?? v.trim()).join(', ');
    }
    return value;
  };

  const renderEditInput = (field: AdmissionField, value: string | undefined, error: string | undefined, set: (v: string) => void) => {
    const common = {
      size: 'small' as const,
      fullWidth: true,
      value: value || '',
      error: !!error,
      helperText: error,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(e.target.value),
    };
    if (field.key === 'classId') {
      return (
        <TextField {...common} select>
          <MenuItem value="">—</MenuItem>
          {classOptions.map(c => <MenuItem key={c._id} value={c.name}>{c.name}</MenuItem>)}
        </TextField>
      );
    }
    if (field.type === 'dropdown' && field.options?.length) {
      return (
        <TextField {...common} select>
          <MenuItem value="">—</MenuItem>
          {field.options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      );
    }
    if (field.type === 'date') return <TextField {...common} type="date" value={(value || '').slice(0, 10)} />;
    if (field.type === 'number') return <TextField {...common} type="number" />;
    if (field.type === 'textarea') return <TextField {...common} multiline maxRows={3} />;
    return <TextField {...common} />;
  };

  const headCell = {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: theme.palette.background.paper,
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(92vh - 360px)', minHeight: 240 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCell, width: 44 }}>Row</TableCell>
              <TableCell sx={{ ...headCell, width: 120 }}>Status</TableCell>
              {columns.map(col => (
                <TableCell key={col.key} sx={{ ...headCell, minWidth: col.type === 'textarea' ? 220 : 150 }}>
                  {col.label}
                  {col.required && <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>}
                </TableCell>
              ))}
              <TableCell sx={{ ...headCell, width: 92, position: 'sticky', right: 0, zIndex: 3 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map(row => {
              const isEditing = editingRowNumber === row.rowNumber && !!draft;
              const errors = isEditing ? draftErrors : row.fieldErrors ?? {};
              const issueCount = Object.keys(row.fieldErrors ?? {}).length || (row.clientStatus === 'error' ? 1 : 0);
              return (
                <TableRow key={row.rowNumber} hover sx={{ verticalAlign: 'top', '& td': { borderColor: theme.palette.divider } }}>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem', pt: 1.5 }}>{row.rowNumber}</TableCell>
                  <TableCell sx={{ pt: 1.25 }}>
                    {row.clientStatus === 'error' ? (
                      <Tooltip
                        arrow
                        title={
                          <Stack spacing={0.25} sx={{ py: 0.25 }}>
                            {Object.entries(row.fieldErrors ?? {}).map(([k, m]) => (
                              <Typography key={k} variant="caption">
                                <b>{columns.find(c => c.key === k)?.label ?? k}</b> — {m}
                              </Typography>
                            ))}
                            {!row.fieldErrors && <Typography variant="caption">{row.clientError}</Typography>}
                          </Stack>
                        }
                      >
                        <Chip
                          size="small"
                          icon={<ErrorOutline sx={{ fontSize: 15 }} />}
                          label={issueCount === 1 ? '1 issue' : `${issueCount} issues`}
                          sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', fontWeight: 600, cursor: 'help' }}
                        />
                      </Tooltip>
                    ) : row.ineligibleReasons?.length ? (
                      <Tooltip arrow title={row.ineligibleReasons.join(' ')}>
                        <Chip
                          size="small"
                          icon={<ErrorOutline sx={{ fontSize: 15 }} />}
                          label="Not eligible"
                          sx={{ bgcolor: alpha(theme.palette.warning.main, 0.14), color: 'warning.dark', fontWeight: 600, cursor: 'help' }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        icon={<CheckCircle sx={{ fontSize: 15 }} />}
                        label="Ready"
                        sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 600 }}
                      />
                    )}
                  </TableCell>

                  {columns.map(col => {
                    const err = errors[col.key];
                    if (isEditing) {
                      return (
                        <TableCell key={col.key} sx={{ py: 1 }}>
                          {renderEditInput(col, draft![col.key], err, (v) => setDraft({ ...draft!, [col.key]: v }))}
                        </TableCell>
                      );
                    }
                    const text = displayValue(col, row.fields[col.key]);
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          pt: 1.5,
                          ...(err && {
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                            boxShadow: `inset 2px 0 0 ${theme.palette.error.main}`,
                          }),
                        }}
                      >
                        <Typography variant="body2" color={text ? 'text.primary' : 'text.disabled'} sx={{ wordBreak: 'break-word' }}>
                          {text || '—'}
                        </Typography>
                        {err && (
                          <Typography variant="caption" color="error.main" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>
                            {err}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}

                  <TableCell
                    align="right"
                    sx={{ position: 'sticky', right: 0, bgcolor: theme.palette.background.paper, pt: 1, whiteSpace: 'nowrap' }}
                  >
                    {isEditing ? (
                      <>
                        <Tooltip title="Save row">
                          <IconButton size="small" color="primary" onClick={() => saveEdit(row.rowNumber)}>
                            <Check fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit}><Close fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit row">
                          <IconButton size="small" onClick={() => startEdit(row)}><EditOutlined fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Remove row">
                          <IconButton size="small" onClick={() => deleteRow(row.rowNumber)} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default BulkApplicationsReviewTable;
